import '@mantine/core/styles.css';
import '@mantine/dates/styles.css';
import '@mantine/notifications/styles.css';
import 'ui-lib/src/lib/css/fonts.css';

import { ProgressChecklistContext } from '@context/progress-checklist-context';
import { getOrCreateContext } from '@context/singleton-manager';
import { TourContext } from '@context/tour-context';
import useSingletonContext from '@hooks/use-singleton-context';
import useTour from '@hooks/use-tour';
import type { NextPage } from 'next';
import type { AppProps /*, AppContext */ } from 'next/app';
import Head from 'next/head';
import { type FC, type ReactElement, useEffect } from 'react';

import '@styles/globals.scss';
import '@reach/skip-nav/styles.css';
import '@reach/dialog/styles.css';

import { ApolloProvider } from '@apollo/client/react';
import NotificationsProvider from '@components/Notifications/NotificationsProvider';
import { DatadogPagesRouter } from '@datadog/browser-rum-nextjs';
import useProgressChecklist from '@hooks/use-progress-checklist';
import { Notifications } from '@mantine/notifications';
import { AnalyticsProvider } from '@modules/analytics/pages-router/AnalyticsProvider';
import { requiresAuth } from '@modules/auth/requires-auth';
import { useOnIdTokenChangedObserver } from '@modules/auth-helpers';
import { cacheCelloReferralCode } from '@modules/cello-referral';
import { AmplitudeExperimentsProvider } from '@modules/feature-flag';
import { RumIdentitySync } from '@modules/monitoring/RumIdentitySync';
import { UserProfileProvider } from '@modules/user-profile/UserProfileProvider';
import { apolloClient } from '@services/apollo';
import dynamic from 'next/dynamic';
import { useRouter } from 'next/router';
import { BlinqMantineProvider } from 'ui-lib/mantine/provider';

// Dynamically imported so unauthenticated pages (login, signup, onboarding)
// don't pay the bundle cost of Framer Motion, date-fns, billing hooks,
// UpgradeAndConsentFlow, and other heavy dependencies that GlobalModalProvider
// pulls in. Only loaded on authenticated routes that actually need modals.
const GlobalModalProvider = dynamic(
  () =>
    import('@modules/global-modals/page-router-providers').then((mod) => mod.GlobalModalProvider),
  { ssr: false },
);

export type NextPageWithLayout<P = Record<string, unknown>, IP = P> = NextPage<P, IP> & {
  requiresAuth?: boolean;

  getLayout?: (page: ReactElement) => React.ReactNode;
};

export type AppPropsWithLayout = AppProps & {
  Component: NextPageWithLayout;
};

const hoverPopoverContext = getOrCreateContext('hover');
const overlayContext = getOrCreateContext('overlay');

const App: FC<AppPropsWithLayout> = ({ Component, pageProps }) => {
  const [popoverContextProps] = useSingletonContext();
  const [overlayContextProps] = useSingletonContext();

  const tourContextProps = useTour();
  const progressChecklistContextProps = useProgressChecklist();

  const router = useRouter();

  const isAuthRoute = requiresAuth({
    pathname: router.pathname,
    hasWorkspace: !!router.query.workspace,
  });

  useEffect(() => {
    const node = document.querySelector('.preload');
    node?.classList?.remove('preload');
  }, []);

  // Cello appends the referral code to whatever destination URL its dashboard is
  // configured with, so this is captured app-wide rather than on one route.
  useEffect(() => {
    if (!router.isReady) {
      return;
    }

    cacheCelloReferralCode(router.query);
  }, [router.isReady, router.query]);

  useOnIdTokenChangedObserver();

  const layout = Component.getLayout ?? ((page: ReactElement) => page);

  const pageContent = (
    <>
      <Head>
        <meta name="viewport" content="width=device-width, initial-scale=1" />
      </Head>

      <NotificationsProvider>
        <overlayContext.Provider value={overlayContextProps}>
          <hoverPopoverContext.Provider value={popoverContextProps}>
            <ProgressChecklistContext.Provider value={progressChecklistContextProps}>
              <TourContext.Provider value={tourContextProps}>
                <BlinqMantineProvider>
                  {layout(
                    <>
                      <Component {...pageProps} />
                      <Notifications zIndex={901} />
                    </>,
                  )}
                </BlinqMantineProvider>
              </TourContext.Provider>
            </ProgressChecklistContext.Provider>
          </hoverPopoverContext.Provider>
        </overlayContext.Provider>
      </NotificationsProvider>
    </>
  );

  return (
    <>
      <DatadogPagesRouter />
      <ApolloProvider client={apolloClient}>
        <UserProfileProvider>
          {(userProfileDetails) => (
            <AmplitudeExperimentsProvider userProfileDetails={userProfileDetails}>
              <>
                <RumIdentitySync />
                {isAuthRoute ? (
                  <GlobalModalProvider>{pageContent}</GlobalModalProvider>
                ) : (
                  pageContent
                )}
                <AnalyticsProvider userProfileDetails={userProfileDetails} />
              </>
            </AmplitudeExperimentsProvider>
          )}
        </UserProfileProvider>
      </ApolloProvider>
    </>
  );
};

// Only uncomment this method if you have blocking data requirements for
// every single page in your application. This disables the ability to
// perform automatic static optimization, causing every page in your app to
// be server-side rendered.
//
// MyApp.getInitialProps = async (appContext: AppContext) => {
//   // calls page's `getInitialProps` and fills `appProps.pageProps`
//   const appProps = await App.getInitialProps(appContext);

//   return { ...appProps }
// }

export default App;
